import { useRef, useState } from 'react'
import QRCode from 'react-qr-code'
import { X, Download, Printer } from 'lucide-react'
import { Asset } from '../types'

interface Props {
  assets: Asset[]
  onClose: () => void
}

// Sticker size in mm, remembered in this browser. Each label prints on its own page of exactly this
// size, which is what label printers expect (one page = one sticker).
const SIZE_KEY = 'assetLabelSize'
const DEFAULT_SIZE = { width: 50, height: 25 }
const clampMm = (n: number, min: number, fallback: number) => Number.isFinite(n) && n >= min ? Math.min(n, 300) : fallback

function loadSize() {
  try {
    const s = JSON.parse(localStorage.getItem(SIZE_KEY) || 'null')
    return { width: clampMm(Number(s?.width), 20, DEFAULT_SIZE.width), height: clampMm(Number(s?.height), 10, DEFAULT_SIZE.height) }
  } catch { return DEFAULT_SIZE }
}

function AssetLabel({ asset, width, height }: { asset: Asset; width: number; height: number }) {
  const padding = Math.min(2, height * 0.08)
  const qrSize = Math.min(height - padding * 2, width * 0.5)
  const gap = padding
  const textWidth = width - qrSize - padding * 2 - gap
  const code = asset.assetCode || asset.name
  const subtitle = [asset.brand, asset.model].filter(Boolean).join(' ')
  // Shrink the code so it stays on one line (bold Arial averages about 0.62em per character)
  const codeFont = Math.min(height * 0.28, textWidth / (Math.max(code.length, 1) * 0.62))

  return (
    <div className="label" style={{ width:`${width}mm`, height:`${height}mm`, padding:`${padding}mm`, display:'flex', alignItems:'center', gap:`${gap}mm`, overflow:'hidden', background:'#fff', color:'#000', boxSizing:'border-box' }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:`${codeFont}mm`, lineHeight:1.1, fontWeight:700, whiteSpace:'nowrap' }}>{code}</div>
        {subtitle && <div style={{ fontSize:`${Math.min(height * 0.1, codeFont)}mm`, lineHeight:1.15, marginTop:`${height * 0.08}mm`, overflowWrap:'anywhere' }}>{subtitle}</div>}
      </div>
      <div className="qr" style={{ flexShrink:0, lineHeight:0 }}>
        <QRCode value={`${window.location.origin}/assets/${asset.id}`} size={256} level="M" fgColor="#000000" bgColor="#FFFFFF"
          style={{ width:`${qrSize}mm`, height:`${qrSize}mm`, display:'block' }}/>
      </div>
    </div>
  )
}

export default function QRCodeModal({ assets, onClose }: Props) {
  const printRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState(loadSize)
  const [widthInput, setWidthInput] = useState(String(size.width))
  const [heightInput, setHeightInput] = useState(String(size.height))
  const single = assets.length === 1 ? assets[0] : null

  const updateSize = (w: string, h: string) => {
    setWidthInput(w); setHeightInput(h)
    const next = { width: clampMm(Number(w), 20, size.width), height: clampMm(Number(h), 10, size.height) }
    setSize(next)
    try { localStorage.setItem(SIZE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
  }

  const handlePrint = () => {
    const content = printRef.current
    if (!content) return
    const w = window.open('', '_blank', 'width=500,height=600')
    if (!w) return
    w.document.write(`
      <html><head><title>Asset Labels</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @page { size: ${size.width}mm ${size.height}mm; margin: 0; }
        body { font-family: -apple-system, Arial, sans-serif; background: white; }
        .label { page-break-after: always; break-after: page; }
        .label:last-child { page-break-after: auto; break-after: auto; }
      </style></head><body>
      ${Array.from(content.querySelectorAll('.label')).map(el => el.outerHTML).join('')}
      <script>window.onload = () => { window.print(); window.close(); }<\/script>
      </body></html>
    `)
    w.document.close()
  }

  const handleDownloadSVG = () => {
    if (!single) return
    const svg = printRef.current?.querySelector('.qr svg') as SVGElement
    if (!svg) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([svgData], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `QR-${single.assetCode || single.id}.svg`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">{single ? 'QR Code Asset' : `พิมพ์ป้าย QR (${assets.length} รายการ)`}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
        </div>

        {/* Sticker size */}
        <div className="flex items-center gap-2 px-5 pt-4 text-sm text-gray-600">
          <span>ขนาดสติกเกอร์</span>
          <input type="number" min={20} max={300} value={widthInput} onChange={e => updateSize(e.target.value, heightInput)} aria-label="กว้าง (mm)"
            className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"/>
          <span>×</span>
          <input type="number" min={10} max={300} value={heightInput} onChange={e => updateSize(widthInput, e.target.value)} aria-label="สูง (mm)"
            className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"/>
          <span>mm</span>
        </div>

        {/* Label preview */}
        <div className="p-5 flex-1 min-h-0 overflow-auto">
          <div ref={printRef} className="flex flex-col items-center gap-2">
            {assets.map(a => (
              <div key={a.id} className="border border-dashed border-gray-300 max-w-full overflow-auto">
                <AssetLabel asset={a} width={size.width} height={size.height}/>
              </div>
            ))}
          </div>

          {/* URL display */}
          {single && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Asset URL</p>
              <p className="text-xs font-mono text-gray-700 break-all">{`${window.location.origin}/assets/${single.id}`}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-5 pt-0">
          {single && (
            <button onClick={handleDownloadSVG} className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-xl text-sm hover:bg-gray-50 transition-colors">
              <Download size={16}/> Download SVG
            </button>
          )}
          <button onClick={handlePrint} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors">
            <Printer size={16}/> {single ? 'Print Label' : `Print ${assets.length} Labels`}
          </button>
        </div>
      </div>
    </div>
  )
}
